/**
 * Streckenvorlagen — die immer gleichen Relationen im Pendelverkehr.
 *
 * Der Sinn steht in einer der frühen Entscheidungen: Wer aus einer Liste
 * wählt, erfindet keine neue Schreibweise. Jede Strecke, die hier gepflegt
 * ist, muss der Fahrer morgens nur antippen statt zweimal einen Ortsnamen zu
 * suchen.
 *
 * Die REIHENFOLGE ist hier keine Kosmetik, deshalb steht sie so prominent.
 * Wenn die Strecke, die einer zwanzig Mal im Monat fährt, auf Platz sieben
 * steht, tippt er sich jeden Morgen dorthin — und irgendwann greift er daneben.
 * Umsortiert wird mit den Pfeilen; die ganze neue Reihenfolge geht in einem
 * Zug zum Server, nicht als zwei vertauschte Werte.
 *
 * Doppelte Strecken lässt die Datenbank nicht zu (dieselbe Kombination aus
 * Lade- und Entladeort gibt es nur einmal). Das ist Absicht: Zwei Vorlagen für
 * dieselbe Fahrt wären in der Auswahlliste nicht zu unterscheiden.
 *
 * ═══ DER PREIS STEHT HIER, DER FAHRER SIEHT IHN NICHT ═══
 *
 * Er liegt in einer eigenen Tabelle mit eigener Zeilensicherheit, nicht als
 * Spalte an der Vorlage. Der Unterschied ist keine Förmlichkeit: Postgres
 * schützt ZEILEN, keine Spalten. Als Spalte müsste jede Abfrage der Fahrer-App
 * sie von Hand weglassen — und beim ersten `select *`, beim ersten neuen
 * Bildschirm stünde der Preis auf dem Handy. „Vergiss nicht, die Spalte
 * wegzulassen" ist keine Zugriffsbeschränkung, sondern eine Bitte.
 *
 * Nachgemessen: Ein Fahrer bekommt aus dieser Tabelle null Zeilen, sieht die
 * Vorlagen selbst aber weiterhin vollständig.
 */

import { useEffect, useMemo, useState } from 'react';
import { Seitenkopf } from '../Rahmen';
import {
  Fehlerkasten,
  Feld,
  Karte,
  Knopf,
  Marker,
  Meldung,
  Tabellenkarte,
  Textfeld,
  Zahlfeld,
} from '../gestaltung/bausteine';
import { Auswahlsuche } from '../gestaltung/Auswahlsuche';
import { useAbfrage } from '../daten/abfrage';
import { anschriftszeile } from '../daten/anschrift';
import { Speicherfehler } from '../daten/speichern';
import { alsKlarfehler, type Klarfehler } from '../daten/fehler';
import { zahlAlsText, zahlLesen } from '../daten/zahl';
import {
  orteLaden,
  vorlageAendern,
  vorlageAnlegen,
  vorlagenLaden,
  vorlagenpreisSetzen,
  vorlagenSortieren,
  type Ort,
  type Vorlagenzeile,
} from '../daten/stammdaten';
import { useEntwurfsfeld, useFormularOffen } from '../daten/formularEntwurf';

export const EINHEITTEXT: Record<string, string> = {
  t: 'Tonne',
  m3: 'm³',
  fuhre: 'Fuhre',
  stueck: 'Stück',
  stunde: 'Stunde',
};

export function StreckenSeite() {
  const liste = useAbfrage(vorlagenLaden, []);
  const orte = useAbfrage(orteLaden, []);

  // Offen bleibt offen — auch über Seitenwechsel und Neuladen (HJ-025).
  const [formularOffen, setFormularOffen] = useFormularOffen('strecke-neu');
  const [fehler, setFehler] = useState<Klarfehler | null>(null);
  const [beschaeftigt, setBeschaeftigt] = useState<string | null>(null);
  const [preisFuer, setPreisFuer] = useState<Vorlagenzeile | null>(null);

  /**
   * Die Reihenfolge wird lokal gehalten, damit ein Pfeilklick sofort sichtbar
   * ist. Der Server bekommt die neue Reihenfolge danach; scheitert das, wird
   * neu geladen und der alte Stand steht wieder da — lieber ein sichtbarer
   * Rücksprung als eine Liste, die anders aussieht, als sie gespeichert ist.
   */
  const [reihenfolge, setReihenfolge] = useState<Vorlagenzeile[]>([]);
  useEffect(() => {
    if (liste.daten) setReihenfolge(liste.daten);
  }, [liste.daten]);

  const aktive = useMemo(() => reihenfolge.filter((v) => v.aktiv), [reihenfolge]);

  // Das Preisformular arbeitet mit der Vorlage, wie sie JETZT in der Liste
  // steht (H1.6). Nach einem Konflikt frischt die Liste auf; das Formular
  // behält die Eingabe (gleicher `key`), bekommt aber den neuen Stand — und
  // zeigt, was die Kollegin inzwischen eingetragen hat.
  const preisVorlage = preisFuer
    ? (liste.daten?.find((v) => v.id === preisFuer.id) ?? preisFuer)
    : null;

  async function mitFehlern(schluessel: string, tun: () => Promise<void>) {
    setFehler(null);
    setBeschaeftigt(schluessel);
    try {
      await tun();
    } catch (problem) {
      setFehler(
        problem instanceof Speicherfehler ? problem.klar : alsKlarfehler(problem),
      );
      liste.auffrischen();
    } finally {
      setBeschaeftigt(null);
    }
  }

  function verschieben(vorlage: Vorlagenzeile, richtung: -1 | 1) {
    const nurAktive = reihenfolge.filter((v) => v.aktiv);
    const stelle = nurAktive.findIndex((v) => v.id === vorlage.id);
    const ziel = stelle + richtung;
    if (stelle < 0 || ziel < 0 || ziel >= nurAktive.length) return;

    const neu = [...nurAktive];
    [neu[stelle], neu[ziel]] = [neu[ziel], neu[stelle]];
    setReihenfolge([...neu, ...reihenfolge.filter((v) => !v.aktiv)]);

    void mitFehlern(`sort:${vorlage.id}`, async () => {
      await vorlagenSortieren(neu.map((v) => v.id));
      // Das Sortieren fasst die Zeilen an und verändert ihren Stand
      // (`geaendert_am`). Ohne Auffrischen hielte die Liste die alten Stände —
      // und ein „Stilllegen" direkt danach scheiterte mit „inzwischen von
      // jemand anderem geändert", obwohl es niemand war.
      liste.auffrischen();
    });
  }

  return (
    <>
      <Seitenkopf
        titel="Streckenvorlagen"
        erklaerung="Was hier steht, muss der Fahrer morgens nur antippen. Die Reihenfolge entscheidet mit: Was oft gefahren wird, gehört nach oben."
        werkzeuge={
          <Knopf art="haupt" onClick={() => setFormularOffen(!formularOffen)}>
            {formularOffen ? 'Abbrechen' : 'Strecke anlegen'}
          </Knopf>
        }
      />

      {fehler && (
        <div style={{ marginBottom: 16 }}>
          <Fehlerkasten fehler={fehler} />
        </div>
      )}

      {preisFuer && (
        <div style={{ marginBottom: 16 }}>
          {/* Der Schlüssel ist hier kein Beiwerk. Ohne ihn hält React beim
              Wechsel von einer Strecke zur nächsten dieselbe Formularinstanz —
              die Überschrift zeigt dann Strecke B, in den Feldern steht aber
              noch der Preis von A, und Speichern schreibt A's Preis an B. */}
          <PreisFormular
            key={preisFuer.id}
            vorlage={preisVorlage!}
            laeuft={beschaeftigt === `preis:${preisFuer.id}`}
            abbrechen={() => setPreisFuer(null)}
            speichern={(werte) =>
              void mitFehlern(`preis:${preisFuer.id}`, async () => {
                await vorlagenpreisSetzen(preisVorlage!, werte);
                setPreisFuer(null);
                liste.auffrischen();
              })
            }
          />
        </div>
      )}

      {formularOffen && (
        <div style={{ marginBottom: 16 }}>
          <AnlegenFormular
            orte={(orte.daten ?? []).filter((o) => o.aktiv)}
            fertig={() => {
              setFormularOffen(false);
              liste.auffrischen();
            }}
          />
        </div>
      )}

      <Tabellenkarte
        titel="Vorlagen in der Reihenfolge der Auswahlliste"
        laedt={liste.laedt}
        fehler={liste.fehler}
        nochmal={liste.auffrischen}
        eintraege={reihenfolge}
        spalten={[
          { text: '' },
          { text: 'Strecke' },
          { text: 'Bezeichnung' },
          { text: 'Preis', rechts: true },
          { text: 'Status' },
          { text: '', rechts: true },
        ]}
        leer="Noch keine Vorlage. Ohne Vorlage muss der Fahrer beide Orte einzeln suchen."
        zeile={(vorlage) => {
          const stelle = aktive.findIndex((v) => v.id === vorlage.id);
          const laeuft = beschaeftigt?.endsWith(vorlage.id) ?? false;
          return (
            <tr
              key={vorlage.id}
              className={vorlage.aktiv ? undefined : 'stillgelegt'}
            >
              <td style={{ whiteSpace: 'nowrap' }}>
                {vorlage.aktiv ? (
                  <div className="reihe" style={{ gap: 4 }}>
                    <button
                      className="pfeil"
                      disabled={stelle <= 0 || laeuft}
                      title="Nach oben"
                      onClick={() => verschieben(vorlage, -1)}
                    >
                      ↑
                    </button>
                    <button
                      className="pfeil"
                      disabled={stelle < 0 || stelle >= aktive.length - 1 || laeuft}
                      title="Nach unten"
                      onClick={() => verschieben(vorlage, 1)}
                    >
                      ↓
                    </button>
                  </div>
                ) : (
                  <span className="stumm">—</span>
                )}
              </td>
              <td>
                <strong>{vorlage.ladeort}</strong>
                <span className="stumm"> → </span>
                <strong>{vorlage.entladeort}</strong>
              </td>
              <td>{vorlage.bezeichnung ?? <span className="stumm">—</span>}</td>
              <td className="tabelle__rechts">
                <button
                  className="zeilenlink"
                  onClick={() => setPreisFuer(vorlage)}
                  title="Preisvorschlag pflegen — der Fahrer sieht ihn nicht."
                >
                  {vorlage.preis_vk !== null ? preisText(vorlage) : 'eintragen'}
                </button>
              </td>
              <td>
                {vorlage.aktiv ? (
                  <Marker art="gut">in der Auswahl</Marker>
                ) : (
                  <Marker art="neutral">stillgelegt</Marker>
                )}
              </td>
              <td className="tabelle__rechts">
                <Knopf
                  art="still"
                  klein
                  disabled={laeuft}
                  title={
                    vorlage.aktiv
                      ? 'Nimmt die Strecke aus der Auswahlliste. Gefahrene Touren bleiben unberührt.'
                      : undefined
                  }
                  onClick={() =>
                    void mitFehlern(`aktiv:${vorlage.id}`, async () => {
                      await vorlageAendern(vorlage, { aktiv: !vorlage.aktiv });
                      liste.auffrischen();
                    })
                  }
                >
                  {vorlage.aktiv ? 'Stilllegen' : 'Zurückholen'}
                </Knopf>
              </td>
            </tr>
          );
        }}
      />
    </>
  );
}

function preisText(vorlage: Vorlagenzeile): string {
  const betrag = (vorlage.preis_vk ?? 0).toLocaleString('de-DE', {
    style: 'currency',
    currency: 'EUR',
  });
  return vorlage.preisart === 'menge'
    ? `${betrag} / ${EINHEITTEXT[vorlage.mengeneinheit ?? ''] ?? vorlage.mengeneinheit}`
    : betrag;
}

function AnlegenFormular(eigenschaften: { orte: Ort[]; fertig: () => void }) {
  // Gleichnamige Orte sind nur an der Anschrift zu unterscheiden (HJ-042).
  const ortsauswahl = eigenschaften.orte.map((o) => ({
    id: o.id,
    name: o.name,
    unter: anschriftszeile(o.strasse, o.plz, o.ortsname),
  }));
  const [ladeortId, setLadeortId] = useEntwurfsfeld<string | null>('strecke-neu', 'ladeortId', null);
  const [entladeortId, setEntladeortId] = useEntwurfsfeld<string | null>(
    'strecke-neu',
    'entladeortId',
    null,
  );
  const [bezeichnung, setBezeichnung] = useEntwurfsfeld('strecke-neu', 'bezeichnung', '');
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<Klarfehler | null>(null);

  const gleich = ladeortId !== null && ladeortId === entladeortId;
  const vollstaendig = ladeortId !== null && entladeortId !== null && !gleich;

  const vorschlag = useMemo(() => {
    const a = eigenschaften.orte.find((o) => o.id === ladeortId)?.name;
    const b = eigenschaften.orte.find((o) => o.id === entladeortId)?.name;
    return a && b ? `${a} → ${b}` : '';
  }, [eigenschaften.orte, ladeortId, entladeortId]);

  async function speichern() {
    if (!vollstaendig || laeuft) return;
    setLaeuft(true);
    setFehler(null);
    try {
      await vorlageAnlegen({
        ladeortId: ladeortId!,
        entladeortId: entladeortId!,
        bezeichnung: bezeichnung.trim() || vorschlag,
      });
      eigenschaften.fertig();
    } catch (problem) {
      const klar =
        problem instanceof Speicherfehler ? problem.klar : alsKlarfehler(problem);
      // Die Datenbank meldet „das gibt es schon" korrekt, aber ohne zu sagen,
      // was gemeint ist. Hier weiß man es.
      setFehler(
        klar.art === 'doppelt'
          ? {
              ...klar,
              titel: 'Diese Strecke gibt es schon',
              text: 'Für dieselben beiden Orte ist bereits eine Vorlage angelegt. Zwei davon wären in der Auswahlliste nicht zu unterscheiden.',
            }
          : klar,
      );
      setLaeuft(false);
    }
  }

  return (
    <Karte titel="Neue Strecke">
      <div className="stapel" style={{ maxWidth: 720 }}>
        {fehler && <Fehlerkasten fehler={fehler} />}

        <div className="feldreihe">
          <Auswahlsuche
            marke="Ladeort"
            wert={ladeortId}
            setzen={setLadeortId}
            auswahl={ortsauswahl}
            disabled={laeuft}
          />
          <Auswahlsuche
            marke="Entladeort"
            wert={entladeortId}
            setzen={setEntladeortId}
            auswahl={ortsauswahl}
            disabled={laeuft}
          />
        </div>

        {gleich && (
          <div className="meldung meldung--warnung">
            <div>Lade- und Entladeort sind derselbe Ort.</div>
          </div>
        )}

        <Textfeld
          marke="Bezeichnung"
          wert={bezeichnung}
          setzen={setBezeichnung}
          platzhalter={vorschlag}
          hinweis="Optional. Bleibt das Feld leer, wird „Ladeort → Entladeort“ genommen."
          disabled={laeuft}
        />

        <div className="reihe reihe--rechts">
          <Knopf art="still" onClick={eigenschaften.fertig}>
            Abbrechen
          </Knopf>
          <Knopf
            art="haupt"
            disabled={!vollstaendig || laeuft}
            onClick={() => void speichern()}
          >
            {laeuft ? 'Wird angelegt …' : 'Anlegen'}
          </Knopf>
        </div>
      </div>
    </Karte>
  );
}

/**
 * Der Preisvorschlag einer Strecke.
 *
 * „Kein Preis" ist ein gültiger Zustand und keine Lücke: Nicht jede Relation
 * hat einen festen Satz. Deshalb entfernt ein leeres Feld den Vorschlag, statt
 * eine Null zu speichern — eine Null wäre ein Preis, und zwar der falscheste.
 *
 * Das Netto-Feld ist ein `Zahlfeld`, kein `<input type="number">` (H1.4). Es
 * war das letzte Preisfeld, das dem Browser das Lesen überließ: „1.250,50"
 * wurde dort als 1,2505 gespeichert, Unsinn als „leer" — und „leer" heißt
 * hier „Vorschlag entfernen". Ein ungültiger Text sperrt jetzt das Speichern;
 * der bisherige Vorschlag bleibt, bis eine Zahl dasteht, die sicher zu lesen
 * ist. Warum nicht geraten wird: `daten/zahl.ts`.
 */
function PreisFormular(eigenschaften: {
  vorlage: Vorlagenzeile;
  laeuft: boolean;
  abbrechen: () => void;
  speichern: (werte: {
    preisart: string | null;
    preis_vk: number | null;
    mengeneinheit: string | null;
  }) => void;
}) {
  const [preisart, setPreisart] = useState(eigenschaften.vorlage.preisart ?? '');
  // Mit Komma, so wie man tippt — „12.5" läse `zahlLesen` zwar richtig, sähe
  // im deutschen Büro aber wie ein Tippfehler aus.
  const [preis, setPreis] = useState(
    zahlAlsText(eigenschaften.vorlage.preis_vk),
  );
  const [einheit, setEinheit] = useState(
    eigenschaften.vorlage.mengeneinheit ?? 't',
  );
  // Der Stand beim Öffnen. Weicht der aktuelle ab, hat jemand anderes den
  // Preis inzwischen geändert, angelegt oder entfernt — und das gehört
  // sichtbar ins Formular, bevor man ein zweites Mal speichert.
  const [standBeimOeffnen] = useState(eigenschaften.vorlage.preis_geaendert_am);
  const fremdGeaendert =
    eigenschaften.vorlage.preis_geaendert_am !== standBeimOeffnen;

  const gelesen = zahlLesen(preis);
  // Ohne Preisart ist das Feld gesperrt und zählt nicht — ein liegengebliebener
  // Text darf dann das Entfernen des Vorschlags nicht blockieren.
  const preisUngueltig = preisart !== '' && gelesen.art === 'ungueltig';

  return (
    <Karte
      titel={`Preis für ${eigenschaften.vorlage.ladeort} → ${eigenschaften.vorlage.entladeort}`}
    >
      <div className="stapel" style={{ maxWidth: 720 }}>
        <p className="meta">
          Dieser Preis ist ein Vorschlag beim Eintragen einer Tour. Er wandert
          einmal in den Auftrag und lebt dort weiter — eine spätere Änderung
          hier macht keine bereits eingetragene Tour rückwirkend teurer.
          <strong> Der Fahrer sieht ihn nicht.</strong>
        </p>

        {fremdGeaendert && (
          <Meldung art="warnung" titel="Inzwischen geändert">
            {eigenschaften.vorlage.preis_vk === null
              ? 'Jemand anderes hat den Preis dieser Strecke inzwischen entfernt.'
              : `Jemand anderes hat inzwischen ${preisText(eigenschaften.vorlage)} eingetragen.`}{' '}
            Speichern ersetzt das jetzt durch deine Eingabe.
          </Meldung>
        )}

        <div className="feldreihe">
          <label className="feld">
            <span className="feld__marke">Preisart</span>
            <select
              value={preisart}
              disabled={eigenschaften.laeuft}
              onChange={(e) => setPreisart(e.target.value)}
            >
              <option value="">Kein fester Preis</option>
              <option value="pauschale">Pauschale</option>
              <option value="menge">Nach Menge</option>
              <option value="stundensatz">Stundensatz</option>
            </select>
          </label>

          <Feld marke="Netto (€)" hinweis="Ohne Umsatzsteuer.">
            <Zahlfeld
              wert={preis}
              setzen={setPreis}
              beschriftung="Netto (€)"
              disabled={eigenschaften.laeuft || preisart === ''}
            />
          </Feld>

          {preisart === 'menge' && (
            <label className="feld">
              <span className="feld__marke">Je Einheit</span>
              <select
                value={einheit}
                disabled={eigenschaften.laeuft}
                onChange={(e) => setEinheit(e.target.value)}
              >
                {Object.entries(EINHEITTEXT).map(([wert, text]) => (
                  <option key={wert} value={wert}>
                    {text}
                  </option>
                ))}
              </select>
            </label>
          )}
        </div>

        <div className="reihe reihe--rechts">
          <Knopf art="still" onClick={eigenschaften.abbrechen}>
            Abbrechen
          </Knopf>
          <Knopf
            art="haupt"
            disabled={eigenschaften.laeuft || preisUngueltig}
            onClick={() => {
              if (preisUngueltig) return;
              eigenschaften.speichern({
                preisart: preisart || null,
                preis_vk: gelesen.art === 'zahl' ? gelesen.wert : null,
                mengeneinheit: einheit,
              });
            }}
          >
            {eigenschaften.laeuft ? 'Wird gespeichert …' : 'Preis speichern'}
          </Knopf>
        </div>
      </div>
    </Karte>
  );
}
